import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { ScanLine, Plus, Minus, Trash2, CheckCircle2, AlertTriangle, Search, X, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function CanteenPOSPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const [q, setQ] = useState("");
  const [student, setStudent] = useState(null);     // {student, wallet, remaining_today, limits}
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);             // [{product, qty}]
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);

  // Super admin needs to pick a school first. Default to demo-school-1 if present.
  useEffect(() => {
    if (!isSuper) return;
    api.get("/schools").then((r) => {
      const list = r.data || [];
      setSchools(list);
      if (list.length) {
        const demo = list.find((s) => s.id === "demo-school-1");
        setSchoolId(demo ? demo.id : list[0].id);
      }
    }).catch(() => {});
  }, [isSuper]);

  const sidParam = isSuper && schoolId ? `?school_id=${schoolId}` : "";

  useEffect(() => {
    if (isSuper && !schoolId) return; // wait until super admin picks a school
    api.get(`/canteen/pos/products${sidParam}`).then((r) => setProducts(r.data || [])).catch(() => {});
  }, [schoolId]); // eslint-disable-line

  const lookup = async (qq) => {
    if (!qq) return;
    if (isSuper && !schoolId) { toast.error(t("select_school_first")); return; }
    try {
      const url = `/canteen/pos/lookup-student?q=${encodeURIComponent(qq)}${isSuper && schoolId ? `&school_id=${schoolId}` : ""}`;
      const { data } = await api.get(url);
      setStudent(data); setCart([]); setValidation(null); setLastOrder(null);
    } catch (e) {
      const raw = e.response?.data?.detail;
      // Map common backend errors to Arabic
      const msg = typeof raw === "string"
        ? (raw === "Student not found" ? t("student_not_found")
          : raw === "Student belongs to another school" ? t("student_other_school")
          : raw)
        : t("student_not_found");
      toast.error(msg);
    }
  };

  const addToCart = (p) => {
    setCart((c) => {
      const existing = c.find((x) => x.product.id === p.id);
      if (existing) return c.map((x) => x.product.id === p.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { product: p, qty: 1 }];
    });
  };
  const setQty = (id, delta) => {
    setCart((c) => c.map((x) => x.product.id === id ? { ...x, qty: Math.max(0, x.qty + delta) } : x).filter((x) => x.qty > 0));
  };
  const removeItem = (id) => setCart((c) => c.filter((x) => x.product.id !== id));

  const cartTotal = cart.reduce((s, x) => s + x.product.price * x.qty, 0);

  const validate = async () => {
    if (!student || cart.length === 0) return null;
    try {
      const { data } = await api.post("/canteen/pos/validate-cart", {
        student_id: student.student.id,
        items: cart.map((x) => ({ product_id: x.product.id, qty: x.qty })),
        ...(isSuper && schoolId ? { school_id: schoolId } : {}),
      });
      setValidation(data); return data;
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); return null; }
  };

  useEffect(() => { if (student && cart.length > 0) validate(); else setValidation(null); }, [cart]); // eslint-disable-line

  const checkout = async () => {
    if (!student || cart.length === 0) return;
    const v = await validate();
    if (!v || !v.ok) {
      toast.error(t("cart_rejected"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/canteen/pos/orders", {
        student_id: student.student.id,
        items: cart.map((x) => ({ product_id: x.product.id, qty: x.qty })),
        ...(isSuper && schoolId ? { school_id: schoolId } : {}),
      });
      setLastOrder(data); setCart([]);
      toast.success(t("order_complete"));
      // refresh student wallet
      lookup(q || student.wallet.qr_token);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (typeof det === "string") {
        toast.error(det);
      } else if (det?.reasons?.length) {
        // Server sent structured cart rejection reasons — translate them
        const lines = det.reasons.map((r) =>
          typeof r === "string" ? r : t(`cart_${r.code}`, r.params || {})
        );
        toast.error(`${t("cart_rejected")}: ${lines.join(" · ")}`);
      } else if (det?.message) {
        // Atomic-update race / etc. — try a code-based translation first
        const codeKey = det.code ? `cart_${det.code}` : null;
        const localized = codeKey ? t(codeKey, det) : det.message;
        toast.error(localized === codeKey ? det.message : localized);
      } else {
        toast.error(t("error"));
      }
    }
    setBusy(false);
  };

  const reset = () => { setStudent(null); setCart([]); setQ(""); setValidation(null); setLastOrder(null); };

  // group products by category
  const grouped = products.reduce((m, p) => { (m[p.category] = m[p.category] || []).push(p); return m; }, {});

  return (
    <div className="space-y-4 max-w-7xl mx-auto" data-testid="canteen-pos-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-semibold flex items-center gap-2">
          <ScanLine className="w-7 h-7 text-primary" /> {t("canteen_pos_title")}
        </h1>
        <div className="flex items-center gap-2">
          {isSuper && schools.length > 0 && (
            <Select value={schoolId} onValueChange={setSchoolId}>
              <SelectTrigger className="w-56" data-testid="pos-school-select">
                <SelectValue placeholder={t("select_school")} />
              </SelectTrigger>
              <SelectContent>
                {schools.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`pos-school-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {student && <Button variant="outline" size="sm" onClick={reset} data-testid="pos-reset"><X className="w-4 h-4 me-1" /> {t("new_transaction")}</Button>}
        </div>
      </div>

      {/* Step 1: search */}
      {!student && (
        <>
          <Card className="p-6 border shadow-none">
            <div className="text-center mb-4">
              <ScanLine className="w-12 h-12 mx-auto text-primary mb-2" />
              <p className="text-sm text-muted-foreground">{t("pos_scan_hint")}</p>
            </div>
            <StudentSearchBox
              q={q} setQ={setQ}
              onSelect={(item) => { setQ(item.qr_token || item.id); lookup(item.qr_token || item.id); }}
              onSubmit={() => lookup(q)}
              schoolId={schoolId} isSuper={isSuper} t={t}
            />
          </Card>

          <TodayPreorders schoolId={schoolId} isSuper={isSuper} t={t} />
        </>
      )}

      {student && (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* LEFT: student + products */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="p-4 border shadow-none flex items-center gap-4 bg-primary/5">
              <div className="w-12 h-12 rounded-full bg-primary/15 grid place-items-center text-primary text-xl font-bold">
                {(student.student.name || "?").charAt(0)}
              </div>
              <div className="flex-1">
                <div className="font-semibold">{student.student.name}</div>
                <div className="text-xs text-muted-foreground">{t("class")}: {student.student.class_id || "—"}</div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-primary text-lg">{student.wallet.balance.toFixed(2)}</div>
                <div className="text-[10px] text-muted-foreground">{t("sar_unit")} · {t("remaining")}: {student.remaining_today.toFixed(2)}</div>
              </div>
            </Card>

            {/* Product grid */}
            {Object.entries(grouped).map(([cat, items]) => (
              <Card key={cat} className="p-4 border shadow-none">
                <h3 className="font-medium text-sm text-muted-foreground mb-2 uppercase tracking-wider">{t(`cat_${cat}`)}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {items.map((p) => {
                    const lowStock = p.stock_qty <= (p.low_stock_threshold || 5);
                    const blocked = student.limits.blocked_categories.includes(p.category) || student.limits.blocked_product_ids.includes(p.id);
                    return (
                      <button key={p.id} type="button"
                        disabled={p.stock_qty === 0 || blocked}
                        onClick={() => addToCart(p)}
                        className={`p-3 rounded-lg border text-start smooth ${blocked || p.stock_qty === 0 ? "opacity-40 cursor-not-allowed" : "hover:border-primary hover:bg-primary/5"}`}
                        data-testid={`prod-${p.id}`}
                      >
                        <div className="flex items-start justify-between gap-1">
                          <div className="font-medium text-sm leading-tight">{p.name_ar}</div>
                          {p.is_healthy && <span className="text-emerald-600 text-xs">✓</span>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{p.price} {t("sar_unit")}</div>
                        {lowStock && p.stock_qty > 0 && <div className="text-[10px] text-amber-600 mt-1">⚠ {p.stock_qty} {t("left")}</div>}
                        {p.stock_qty === 0 && <div className="text-[10px] text-red-600 mt-1">{t("out_of_stock")}</div>}
                        {blocked && <div className="text-[10px] text-red-600 mt-1">{t("blocked_by_parent")}</div>}
                      </button>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>

          {/* RIGHT: cart */}
          <div className="space-y-3">
            <Card className="p-4 border shadow-none">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Wallet className="w-4 h-4" /> {t("cart")}</h3>
              {cart.length === 0 ? (
                <div className="text-center py-6 text-sm text-muted-foreground">{t("empty_cart")}</div>
              ) : (
                <div className="space-y-2">
                  {cart.map(({ product, qty }) => (
                    <div key={product.id} className="flex items-center gap-2 text-sm border-b border-border pb-2">
                      <div className="flex-1">
                        <div className="font-medium">{product.name_ar}</div>
                        <div className="text-xs text-muted-foreground">{product.price} × {qty} = {(product.price * qty).toFixed(2)}</div>
                      </div>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setQty(product.id, -1)} data-testid={`dec-${product.id}`}><Minus className="w-3 h-3" /></Button>
                      <span className="font-mono w-6 text-center">{qty}</span>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setQty(product.id, +1)} data-testid={`inc-${product.id}`}><Plus className="w-3 h-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(product.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between font-bold pt-2">
                    <span>{t("total")}</span>
                    <span className="text-primary text-lg">{cartTotal.toFixed(2)} {t("sar_unit")}</span>
                  </div>
                </div>
              )}
              {/* Validation feedback */}
              {validation && validation.blocked_reasons?.length > 0 && (
                <div className="mt-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 rounded-lg p-3 text-xs space-y-1">
                  <div className="flex items-center gap-1 font-semibold text-amber-700">
                    <AlertTriangle className="w-3.5 h-3.5" /> {t("issues")}
                  </div>
                  {validation.blocked_reasons.map((r, i) => {
                    // New shape: { code, params }. Legacy fallback: string.
                    const text = typeof r === "string"
                      ? r
                      : t(`cart_${r.code}`, r.params || {});
                    return <div key={i} className="text-amber-700">• {text}</div>;
                  })}
                </div>
              )}
              {validation && validation.ok && (
                <div className="mt-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 rounded-lg p-2 text-xs flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {t("ready_to_pay")}
                </div>
              )}
              <Button
                className="w-full mt-3 h-11"
                onClick={checkout}
                disabled={cart.length === 0 || busy || (validation && !validation.ok)}
                data-testid="pos-checkout"
              >
                {busy ? "..." : `${t("pay_from_wallet")} ${cartTotal > 0 ? `(${cartTotal.toFixed(2)})` : ""}`}
              </Button>
            </Card>

            {lastOrder && (
              <Card className="p-4 border-2 border-emerald-500/30 bg-emerald-500/5 shadow-none">
                <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2">
                  <CheckCircle2 className="w-5 h-5" /> {t("order_complete")}
                </div>
                <div className="text-xs text-muted-foreground">#{lastOrder.id.slice(0, 8)} · {lastOrder.total.toFixed(2)} {t("sar_unit")}</div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TodayPreorders({ schoolId, isSuper, t }) {
  const [list, setList] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const sidParam = isSuper && schoolId ? `&school_id=${schoolId}` : "";

  const load = async () => {
    try {
      const { data } = await api.get(`/canteen/preorders?status=pending&scheduled_for=${today}${sidParam}`);
      setList(data || []);
    } catch { setList([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [schoolId]);

  const deliver = async (id) => {
    try {
      await api.post(`/canteen/preorders/${id}/deliver`);
      toast.success(t("delivered"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (list.length === 0) return null;
  return (
    <Card className="p-4 border shadow-none" data-testid="pos-today-preorders">
      <h3 className="font-semibold mb-3 flex items-center gap-2">
        <ScanLine className="w-4 h-4 text-primary" /> {t("preorder_today")} ({list.length})
      </h3>
      <ul className="divide-y border rounded-lg">
        {list.map((p) => (
          <li key={p.id} className="p-3 flex items-center justify-between text-sm" data-testid={`preorder-${p.id}`}>
            <div>
              <div className="font-semibold">{p.student_name || p.student_id.slice(0, 8)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {p.items.map((it) => `${it.product_name_ar} × ${it.qty}`).join(" · ")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-semibold">{p.total.toFixed(2)} {t("sar_unit")}</span>
              <Button size="sm" onClick={() => deliver(p.id)} data-testid={`deliver-${p.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 me-1" /> {t("deliver")}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}


/**
 * Debounced student-name search box with a dropdown of matches.
 * Falls back to the legacy "type-and-press-Enter" behavior for QR scans.
 */
function StudentSearchBox({ q, setQ, onSelect, onSubmit, schoolId, isSuper, t }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);

  // Debounced fetch — only triggers after the user pauses typing.
  useEffect(() => {
    const v = (q || "").trim();
    if (v.length < 2 || /^[A-Za-z0-9_-]{16,}$/.test(v)) {
      // looks like a QR token / short input — skip suggestions
      setResults([]); setOpen(false); return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const sidParam = isSuper && schoolId ? `&school_id=${schoolId}` : "";
        const { data } = await api.get(
          `/canteen/pos/students/search?q=${encodeURIComponent(v)}&limit=8${sidParam}`
        );
        setResults(data || []); setOpen((data || []).length > 0);
        setActive(0);
      } catch { setResults([]); setOpen(false); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(handle);
  }, [q, schoolId, isSuper]);

  const onKey = (e) => {
    if (open && results.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % results.length); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); return; }
      if (e.key === "Enter") {
        e.preventDefault();
        const sel = results[active];
        if (sel) onSelect(sel);
        setOpen(false);
        return;
      }
      if (e.key === "Escape") { setOpen(false); return; }
    } else if (e.key === "Enter") {
      onSubmit();
    }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="pos-search-box">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            autoFocus value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            onFocus={() => results.length > 0 && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            placeholder={t("pos_search_placeholder")} className="ps-10"
            data-testid="pos-search"
            autoComplete="off"
          />
        </div>
        <Button onClick={onSubmit} data-testid="pos-search-btn">{t("search")}</Button>
      </div>

      {open && (
        <div className="relative">
          <Card className="absolute z-20 inset-x-0 mt-1 border shadow-lg max-h-80 overflow-y-auto" data-testid="pos-search-dropdown">
            {loading && results.length === 0 && (
              <div className="px-4 py-3 text-sm text-muted-foreground">{t("loading")}</div>
            )}
            {results.length === 0 && !loading && (
              <div className="px-4 py-3 text-sm text-muted-foreground">{t("no_results")}</div>
            )}
            <ul>
              {results.map((s, i) => (
                <li
                  key={s.id}
                  onMouseDown={(e) => { e.preventDefault(); onSelect(s); }}
                  onMouseEnter={() => setActive(i)}
                  data-testid={`pos-suggestion-${s.id}`}
                  className={`px-4 py-2.5 cursor-pointer text-sm flex items-center justify-between gap-3 ${
                    i === active ? "bg-primary/10" : "hover:bg-muted/60"
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                      {(s.name || "?").slice(0, 1)}
                    </div>
                    <span className="truncate">{s.name || s.id}</span>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground shrink-0">
                    {s.balance.toFixed(2)} {t("sar_unit")}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}
    </div>
  );
}

